import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError, downloadBlob, API_BASE } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  Plus, RefreshCw, Search, Pencil, Archive, ArchiveRestore, Trash2, Download,
  FileSpreadsheet, MessageSquareWarning, Heart, AlertTriangle, MessageSquare, Sparkles,
} from "lucide-react";
import RecordDialog from "@/components/RecordDialog";
import { MODULES } from "@/config/modules";
import { useAuth } from "@/context/AuthContext";
import MobileFeedbackList from "@/components/mobile/MobileFeedbackList";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { MoreVertical } from "lucide-react";

const KIND_META = {
  compliment: { label: "Compliment", icon: Heart, cls: "bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300 dark:border-emerald-800" },
  complaint: { label: "Complaint", icon: MessageSquareWarning, cls: "bg-orange-50 text-orange-800 border-orange-200 dark:bg-orange-950/40 dark:text-orange-300 dark:border-orange-800" },
};

const STATUS_META = {
  draft: { label: "Draft", cls: "bg-slate-100 text-slate-700 border-slate-300 dark:bg-slate-800 dark:text-slate-300 dark:border-slate-600" },
  open: { label: "Open", cls: "bg-red-50 text-red-700 border-red-300 dark:bg-red-950/40 dark:text-red-300 dark:border-red-800" },
  in_progress: { label: "In Progress", cls: "bg-amber-50 text-amber-800 border-amber-300 dark:bg-amber-950/40 dark:text-amber-300 dark:border-amber-800" },
  closed: { label: "Closed", cls: "bg-emerald-50 text-emerald-700 border-emerald-300 dark:bg-emerald-950/40 dark:text-emerald-300 dark:border-emerald-800" },
  resolved: { label: "Resolved", cls: "bg-emerald-50 text-emerald-700 border-emerald-300 dark:bg-emerald-950/40 dark:text-emerald-300 dark:border-emerald-800" },
};

const PRIORITY_META = {
  High: "bg-red-50 text-red-700 border-red-200",
  Medium: "bg-amber-50 text-amber-800 border-amber-200",
  Low: "bg-emerald-50 text-emerald-700 border-emerald-200",
};

const daysBetween = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return null;
  return Math.floor((Date.now() - d.getTime()) / 86400000);
};

export default function FeedbackCentre() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [kpis, setKpis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [showArchived, setShowArchived] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [detail, setDetail] = useState(null);

  const module = MODULES.complaints;

  const load = async () => {
    setLoading(true);
    try {
      const [r, k] = await Promise.all([
        api.get("/complaints"),
        api.get("/feedback/kpis"),
      ]);
      setRows(r.data || []);
      setKpis(k.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return rows
      .filter((r) => showArchived ? true : !r.archived)
      .filter((r) => statusFilter === "all" ? true : (r.status === statusFilter))
      .filter((r) => {
        if (!q) return true;
        return [r.subject, r.source, r.service_user, r.assigned_to, r.kind, r.description, r.resolution]
          .some((v) => v && String(v).toLowerCase().includes(q));
      });
  }, [rows, query, statusFilter, showArchived]);

  const KPI_CARDS = [
    { key: "total_complaints", label: "Complaints", tone: "bg-orange-50 text-orange-800 border-orange-200", icon: MessageSquareWarning },
    { key: "total_compliments", label: "Compliments", tone: "bg-emerald-50 text-emerald-700 border-emerald-200", icon: Heart },
    { key: "open_cases", label: "Open Cases", tone: "bg-red-50 text-red-700 border-red-200", icon: AlertTriangle },
    { key: "closed_cases", label: "Closed Cases", tone: "bg-slate-50 text-slate-700 border-slate-200", icon: MessageSquare },
  ];

  const handleSubmit = async (values) => {
    try {
      if (editing) await api.put(`/complaints/${editing.id}`, { data: values });
      else await api.post("/complaints", { data: values });
      toast.success(editing ? "Feedback updated" : "Feedback recorded");
      setEditing(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); throw e; }
  };

  const archive = async (r) => {
    if (!window.confirm(`Archive this ${r.kind}? It stays on record for CQC governance but is hidden from the active list.`)) return;
    try {
      await api.post(`/feedback/${r.id}/archive`, { reason: "" });
      toast.success("Archived");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const restore = async (r) => {
    try {
      await api.post(`/feedback/${r.id}/restore`);
      toast.success("Restored");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const hardDelete = async (r) => {
    if (!window.confirm("Permanently delete this record? Only admins can perform this action — it will be audit-logged.")) return;
    try {
      await api.delete(`/feedback/${r.id}`);
      toast.success("Permanently deleted");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const exportXlsx = async () => {
    try {
      await downloadBlob("/feedback/export.xlsx", `ayobell-feedback-${new Date().toISOString().slice(0,10)}.xlsx`);
      toast.success("Excel downloaded");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const exportPdf = async () => {
    try {
      await downloadBlob("/reports/module/complaints", "ayobell-feedback.pdf");
      toast.success("PDF downloaded");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const isAdmin = user?.role === "admin";
  const canManage = ["admin", "manager", "registered_manager", "deputy_manager", "service_manager", "super_admin"].includes(user?.role);

  return (
    <div className="flex-1 min-h-0 flex flex-col">
      {/* Batch 6.1 · Mobile compact header ( <lg ). Reuses the SAME
          handlers desktop uses. Care workers do not receive manager
          handlers so New / Export / Edit / Archive / Restore / Delete
          controls are omitted for them exactly as they are on desktop. */}
      <div className="lg:hidden border-b border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 px-3 py-2 shrink-0 space-y-2">
        <div className="flex items-center justify-between gap-2">
          <div className="min-w-0">
            <div className="text-[9px] uppercase tracking-widest text-slate-500 font-semibold">Governance</div>
            <h1 className="font-heading text-base font-semibold truncate" data-testid="mobile-page-title">Feedback Centre</h1>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            {canManage && (
              <Button onClick={() => { setEditing(null); setDialogOpen(true); }} data-testid="mobile-btn-new-feedback" className="h-11 bg-sky-700 hover:bg-sky-800 text-white">
                <Plus className="w-4 h-4 mr-1" /> New
              </Button>
            )}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  aria-label="More actions"
                  data-testid="mobile-feedback-more"
                  className="inline-flex items-center justify-center w-11 h-11 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-700 dark:text-slate-200"
                >
                  <MoreVertical className="w-5 h-5" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuLabel className="text-[10px] uppercase tracking-widest text-slate-500">Actions</DropdownMenuLabel>
                <DropdownMenuItem onSelect={load} data-testid="mobile-btn-refresh"><RefreshCw className="w-4 h-4 mr-2" /> Refresh</DropdownMenuItem>
                {canManage && (
                  <>
                    <DropdownMenuItem onSelect={exportXlsx} data-testid="mobile-btn-export-xlsx"><FileSpreadsheet className="w-4 h-4 mr-2" /> Export Excel</DropdownMenuItem>
                    <DropdownMenuItem onSelect={exportPdf} data-testid="mobile-btn-export-pdf"><Download className="w-4 h-4 mr-2" /> Export PDF</DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            placeholder="Search subject, person, service user…"
            value={query} onChange={(e) => setQuery(e.target.value)}
            data-testid="mobile-feedback-search"
            className="h-11 pl-9 bg-white dark:bg-slate-900"
          />
        </div>
      </div>

      {/* Desktop toolbar ( ≥ lg ) — unchanged from pre-Batch 6.1. */}
      <div className="hidden lg:flex h-14 border-b border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-6 items-center justify-between shrink-0">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-slate-500">Governance</div>
          <h1 className="font-heading text-base font-semibold" data-testid="page-title">Feedback Centre</h1>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              placeholder="Search subject, person, service user, family, type…"
              value={query} onChange={(e) => setQuery(e.target.value)}
              data-testid="feedback-search"
              className="w-96 h-9 pl-8 text-sm bg-white dark:bg-slate-900"
            />
          </div>
          <Button variant="outline" size="sm" onClick={load} data-testid="btn-refresh" className="h-9"><RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Refresh</Button>
          <Button variant="outline" size="sm" onClick={exportXlsx} data-testid="btn-export-xlsx" className="h-9"><FileSpreadsheet className="w-3.5 h-3.5 mr-1.5" /> Export Excel</Button>
          <Button variant="outline" size="sm" onClick={exportPdf} data-testid="btn-export-pdf" className="h-9"><Download className="w-3.5 h-3.5 mr-1.5" /> Export PDF</Button>
          <Button size="sm" onClick={() => { setEditing(null); setDialogOpen(true); }} data-testid="btn-new-feedback" className="h-9 bg-sky-700 hover:bg-sky-800"><Plus className="w-3.5 h-3.5 mr-1.5" /> New Record</Button>
        </div>
      </div>

      <div className="flex-1 overflow-auto bg-slate-50 dark:bg-slate-950 p-6 space-y-4">
        {/* KPI cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="feedback-kpis">
          {KPI_CARDS.map((c) => {
            const Icon = c.icon;
            return (
              <div key={c.key} className={`border ${c.tone} rounded-lg px-4 py-3 flex items-center gap-3`} data-testid={`kpi-${c.key}`}>
                <div className="p-2 rounded-md bg-white/60"><Icon className="w-4 h-4" /></div>
                <div>
                  <div className="text-[10px] uppercase tracking-widest font-semibold opacity-80">{c.label}</div>
                  <div className="text-2xl font-heading font-semibold tabular-nums leading-tight">{kpis?.[c.key] ?? "—"}</div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Filters */}
        <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-md p-3 flex items-center flex-wrap gap-2">
          <span className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mr-2">Filter</span>
          {["all", "open", "in_progress", "closed", "draft"].map((s) => (
            <button
              key={s}
              onClick={() => setStatusFilter(s)}
              data-testid={`filter-${s}`}
              className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${statusFilter === s ? "bg-sky-700 text-white border-sky-700" : "bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 border-slate-300 dark:border-slate-600"}`}
            >
              {s === "all" ? "All" : STATUS_META[s]?.label || s}
            </button>
          ))}
          <div className="flex-1" />
          <label className="text-xs text-slate-600 dark:text-slate-300 inline-flex items-center gap-1.5 cursor-pointer">
            <input type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} data-testid="toggle-archived" />
            Show archived ({kpis?.archived ?? 0})
          </label>
          {kpis?.stale_over_28d > 0 && (
            <span className="ml-3 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-red-50 text-red-700 border border-red-200" data-testid="stale-warning">
              <AlertTriangle className="w-3 h-3" /> {kpis.stale_over_28d} open &gt;28 days
            </span>
          )}
        </div>

        {/* Mobile card list ( <lg ) — same rows, same handlers, same
            per-role gating; care workers do not receive manager handlers
            so no edit/archive/delete controls render for them. */}
        <div className="lg:hidden">
          <MobileFeedbackList
            loading={loading}
            rows={filtered}
            isAdmin={isAdmin}
            onOpenDetail={(r) => setDetail(r)}
            onEdit={canManage ? (r) => { setEditing(r); setDialogOpen(true); } : undefined}
            onArchive={canManage ? archive : undefined}
            onRestore={canManage ? restore : undefined}
            onHardDelete={isAdmin ? hardDelete : undefined}
          />
        </div>

        {/* Desktop Table ( ≥ lg ) — unchanged from pre-Batch 6.1. */}
        <div className="hidden lg:block bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-md overflow-x-auto">
          {loading ? (
            <div className="p-8 text-sm text-slate-500">Loading…</div>
          ) : filtered.length === 0 ? (
            <div className="p-16 text-center text-sm text-slate-500" data-testid="feedback-empty">No feedback records match your filter.</div>
          ) : (
            <table className="data-table" data-testid="feedback-table">
              <thead>
                <tr>
                  <th className="row-num">#</th>
                  <th>Kind</th>
                  <th>Received</th>
                  <th>From</th>
                  <th>Subject</th>
                  <th>Service User</th>
                  <th>Priority</th>
                  <th>Assigned</th>
                  <th>Status</th>
                  <th className="w-32 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r, i) => {
                  const kind = KIND_META[r.kind] || KIND_META.complaint;
                  const KindIcon = kind.icon;
                  const status = STATUS_META[r.status] || STATUS_META.draft;
                  const days = daysBetween(r.received_date);
                  const stale = r.kind === "complaint" && r.status !== "closed" && r.status !== "resolved" && days != null && days > 28;
                  return (
                    <tr
                      key={r.id}
                      data-testid={`feedback-row-${r.id}`}
                      onClick={() => setDetail(r)}
                      className={`cursor-pointer hover:bg-sky-50/40 dark:hover:bg-slate-800/40 ${r.archived ? "opacity-60" : ""}`}
                    >
                      <td className="row-num">{i + 1}</td>
                      <td>
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-medium rounded-full border ${kind.cls}`}>
                          <KindIcon className="w-3 h-3" /> {kind.label}
                        </span>
                      </td>
                      <td className="tabular-nums text-xs">{r.received_date || "—"}</td>
                      <td className="text-sm">{r.source || <span className="text-slate-300">—</span>}</td>
                      <td className="text-sm font-medium">{r.subject || <span className="text-slate-300">—</span>}</td>
                      <td className="text-xs text-slate-600">{r.service_user || <span className="text-slate-300">—</span>}</td>
                      <td>
                        {r.priority ? (
                          <span className={`inline-flex px-2 py-0.5 text-[11px] font-medium rounded-full border ${PRIORITY_META[r.priority] || "bg-slate-50 text-slate-500 border-slate-200"}`}>{r.priority}</span>
                        ) : <span className="text-slate-300">—</span>}
                      </td>
                      <td className="text-xs">{r.assigned_to || <span className="text-slate-300">—</span>}</td>
                      <td>
                        <div className="flex items-center gap-1.5">
                          <span className={`inline-flex px-2 py-0.5 text-[11px] font-medium rounded-full border ${status.cls}`}>{status.label}</span>
                          {stale && (
                            <span
                              className="inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[10px] font-semibold rounded-full bg-red-100 text-red-800 border border-red-300"
                              title={`Open for ${days} days`}
                              data-testid={`stale-${r.id}`}
                            >
                              <AlertTriangle className="w-2.5 h-2.5" /> {days}d
                            </span>
                          )}
                          {r.archived && <span className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Archived</span>}
                        </div>
                      </td>
                      <td className="text-right whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                        <button onClick={() => { setEditing(r); setDialogOpen(true); }} className="p-1 text-slate-500 hover:text-sky-700" data-testid={`btn-edit-${r.id}`} title="Edit"><Pencil className="w-3.5 h-3.5" /></button>
                        {!r.archived ? (
                          <button onClick={() => archive(r)} className="p-1 text-slate-500 hover:text-amber-700 ml-0.5" data-testid={`btn-archive-${r.id}`} title="Archive"><Archive className="w-3.5 h-3.5" /></button>
                        ) : (
                          <button onClick={() => restore(r)} className="p-1 text-slate-500 hover:text-emerald-700 ml-0.5" data-testid={`btn-restore-${r.id}`} title="Restore"><ArchiveRestore className="w-3.5 h-3.5" /></button>
                        )}
                        {isAdmin && (
                          <button onClick={() => hardDelete(r)} className="p-1 text-slate-500 hover:text-red-600 ml-0.5" data-testid={`btn-hard-delete-${r.id}`} title="Permanently delete (admin only)"><Trash2 className="w-3.5 h-3.5" /></button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <RecordDialog
        open={dialogOpen}
        onOpenChange={(v) => { setDialogOpen(v); if (!v) setEditing(null); }}
        module={module}
        record={editing}
        onSubmit={handleSubmit}
      />

      <FeedbackDetailDrawer record={detail} onClose={() => setDetail(null)} onEdit={(r) => { setDetail(null); setEditing(r); setDialogOpen(true); }} />
    </div>
  );
}

function FeedbackDetailDrawer({ record, onClose, onEdit }) {
  if (!record) return null;
  const kind = KIND_META[record.kind] || KIND_META.complaint;
  const status = STATUS_META[record.status] || STATUS_META.draft;
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/40 flex justify-end" onClick={onClose} data-testid="feedback-detail-drawer">
      <div className="bg-white dark:bg-slate-900 w-full max-w-lg h-full overflow-y-auto shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="border-b border-slate-200 dark:border-slate-700 px-5 py-3 flex items-center justify-between sticky top-0 bg-white dark:bg-slate-900">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Feedback record</div>
            <h3 className="font-heading text-lg font-semibold">{record.subject || "—"}</h3>
          </div>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-900" data-testid="detail-close">&times;</button>
        </div>
        <div className="p-5 space-y-3 text-sm">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`inline-flex px-2 py-0.5 text-[11px] font-medium rounded-full border ${kind.cls}`}>{kind.label}</span>
            <span className={`inline-flex px-2 py-0.5 text-[11px] font-medium rounded-full border ${status.cls}`}>{status.label}</span>
            {record.priority && <span className={`inline-flex px-2 py-0.5 text-[11px] font-medium rounded-full border ${PRIORITY_META[record.priority]}`}>{record.priority} Priority</span>}
          </div>
          <Row label="Received" value={record.received_date} />
          <Row label="From" value={record.source} />
          <Row label="Service User" value={record.service_user} />
          <Row label="Assigned" value={record.assigned_to} />
          <Row label="Description" value={record.description} multi />
          <Row label="Resolution" value={record.resolution} multi />
          {record.archived && (
            <div className="mt-2 p-2 rounded-md bg-slate-100 dark:bg-slate-800 text-xs text-slate-600 dark:text-slate-300">
              Archived {record.archived_at?.slice(0, 10)} by {record.archived_by}
            </div>
          )}
        </div>
        <div className="border-t border-slate-200 dark:border-slate-700 px-5 py-3 flex items-center justify-end gap-2">
          <Button variant="outline" onClick={onClose} data-testid="detail-cancel">Close</Button>
          <Button onClick={() => onEdit(record)} className="bg-sky-700 hover:bg-sky-800" data-testid="detail-edit">Edit record</Button>
        </div>
      </div>
    </div>
  );
}

function Row({ label, value, multi }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{label}</div>
      <div className={`text-slate-800 dark:text-slate-200 mt-0.5 ${multi ? "whitespace-pre-wrap" : ""}`}>
        {value || <span className="text-slate-300">—</span>}
      </div>
    </div>
  );
}
